import api from "../services/api";

// Stock check of the invoice lines being entered, the same rule the server applies when the invoice is saved: a line with a
// warehouse needs its quantity (all lines of that product and warehouse together) in that warehouse; a line without one needs
// it in at least one sellable (good) warehouse.

/** Stock of each product per warehouse, keyed by product id: { total, warehouses: [{ warehouseId, warehouseName, warehouseType, quantity }] }. */
export const fetchAvailability = async (productIds) => {
  const ids = [...new Set(productIds.filter(Boolean).map(Number))];
  if (!ids.length) return {};
  const rows = await api.get("/api/v1/inventory/availability", { productIds: ids.join(",") });
  return Object.fromEntries((rows || []).map((row) => [row.productId, row]));
};

const qty = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

/** What the line can draw on: the picked warehouse's stock, else the most any one good warehouse holds. */
export const availableFor = (stock, warehouseId) => {
  if (!stock) return null;
  if (warehouseId) return Number(stock.warehouses.find((w) => String(w.warehouseId) === String(warehouseId))?.quantity || 0);
  return Math.max(0, ...stock.warehouses.filter((w) => w.warehouseType !== "BAD").map((w) => Number(w.quantity || 0)));
};

/** Problems with the lines (empty when the stock covers them). `items`: [{ productId, quantity, warehouseId }]. */
export const stockShortfalls = (items, stockByProduct) => {
  const needed = new Map();
  items.forEach((item) => {
    if (!item.productId || !(Number(item.quantity) > 0)) return;
    const key = `${item.productId}:${item.warehouseId || ""}`;
    needed.set(key, (needed.get(key) || 0) + Number(item.quantity));
  });
  const problems = [];
  needed.forEach((quantity, key) => {
    const [productId, warehouseId] = key.split(":");
    const stock = stockByProduct[productId];
    if (!stock) return;
    const available = availableFor(stock, warehouseId);
    if (available + 1e-9 < quantity) {
      const where = warehouseId
        ? stock.warehouses.find((w) => String(w.warehouseId) === warehouseId)?.warehouseName || "the warehouse"
        : "any warehouse";
      problems.push(`${stock.productName}: ${qty(available)} available in ${where}, ${qty(quantity)} needed`);
    }
  });
  return problems;
};

export const formatQty = qty;
